import { Layer } from 'grommet';
import { XIcon } from 'lucide-preact';
import { useEffect, useState } from 'preact/hooks';

import { ParsedUserProduct } from '@/queries/products/types';

import { Header, HeaderTitle, Section } from '../Section';
import RecurringProductDetails from './RecurringProductDetails';
import SponsorshipProductDetails from './SponsorshipProductDetails';

interface UserProductDetailsModalProps {
  userProduct: ParsedUserProduct;
  onClose: () => void;
}

const UserProductDetailsModal = ({ userProduct, onClose }: UserProductDetailsModalProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const isSupported = userProduct.productKind === 'recurring' || userProduct.productKind === 'sponsorship';

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsMobile(document.body.classList.contains('group/mobile-layout'));
        }
      });
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    // Initial check
    setIsMobile(document.body.classList.contains('group/mobile-layout'));
    // Cleanup
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // immediately close the modal if the product is not supported
    if (!isSupported) {
      onClose();
    }
  }, [isSupported, onClose]);

  if (!isSupported) {
    return null;
  }

  return (
    <Layer
      // key is used to force re-render the modal when the layout changes
      key={isMobile ? 'mobile' : 'desktop'}
      full={isMobile ? 'horizontal' : undefined}
      animation={isMobile ? 'slide' : 'fadeIn'}
      position={isMobile ? 'bottom' : undefined}
      className="!overflow-auto !rounded-t-2xl mobile:!max-w-full desktop:!w-[calc(100%-3rem)] desktop:!max-w-[1098px] desktop:!rounded-b-2xl"
      onEsc={onClose}
      onClickOutside={onClose}
    >
      <Section>
        <Header className="mobile:px-3">
          <div className="flex items-center justify-between">
            <HeaderTitle>Purchase Details</HeaderTitle>
            <button
              type="button"
              className="preflight preflight-button flex items-center justify-center gap-3"
              onClick={onClose}
            >
              <XIcon className="size-5" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </Header>
        {userProduct.productKind === 'recurring' ? (
          <RecurringProductDetails userProduct={userProduct} />
        ) : userProduct.productKind === 'sponsorship' ? (
          <SponsorshipProductDetails userProduct={userProduct} />
        ) : null}
      </Section>
    </Layer>
  );
};

export default UserProductDetailsModal;
